import {
  useCallback,
  useRef,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import type { ArrowDirection } from '../../components/atomic/buttons/arrows/Arrows';
import { clientDeltaToDesign } from './designPointer';

export type SwipeAxis = 'horizontal' | 'vertical';

export type UseSwipeNavigateOptions = {
  axis: SwipeAxis;
  onSwipe: (direction: ArrowDirection) => void;
  /** Skip gesture while false (e.g. modal open). Default true. */
  enabled?: boolean;
  /** Minimum design-px movement to count as a swipe. */
  threshold?: number;
};

const DEFAULT_THRESHOLD = 56;
/** How long after a swipe to ignore the synthetic click from the same gesture. */
const SUPPRESS_CLICK_MS = 400;

type PointerSession = {
  pointerId: number;
  startClientX: number;
  startClientY: number;
};

/**
 * Discrete swipe → ArrowDirection for screens that already navigate via Arrows.
 * Suppresses the following click when a swipe was recognized (so cards/buttons
 * do not fire onClick after a page change).
 */
export function useSwipeNavigate({
  axis,
  onSwipe,
  enabled = true,
  threshold = DEFAULT_THRESHOLD,
}: UseSwipeNavigateOptions) {
  const sessionRef = useRef<PointerSession | null>(null);
  /** Timestamp until which capture-phase clicks are swallowed (0 = off). */
  const suppressClickUntilRef = useRef(0);
  const onSwipeRef = useRef(onSwipe);
  onSwipeRef.current = onSwipe;

  const resolveDirection = useCallback(
    (dx: number, dy: number): ArrowDirection | null => {
      if (axis === 'horizontal') {
        if (Math.abs(dx) < threshold || Math.abs(dx) < Math.abs(dy)) {
          return null;
        }
        return dx > 0 ? 'left' : 'right';
      }
      if (Math.abs(dy) < threshold || Math.abs(dy) < Math.abs(dx)) {
        return null;
      }
      return dy > 0 ? 'up' : 'down';
    },
    [axis, threshold],
  );

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if (!enabled || event.button !== 0) {
        return;
      }
      const target = event.target;
      if (
        target instanceof Element &&
        target.closest('button, a, input, textarea, select, [role="button"]')
      ) {
        return;
      }
      sessionRef.current = {
        pointerId: event.pointerId,
        startClientX: event.clientX,
        startClientY: event.clientY,
      };
    },
    [enabled],
  );

  const onPointerUp = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      const session = sessionRef.current;
      if (!session || session.pointerId !== event.pointerId) {
        return;
      }
      sessionRef.current = null;

      if (!enabled) {
        return;
      }

      const { dx, dy } = clientDeltaToDesign(
        event.currentTarget,
        event.clientX - session.startClientX,
        event.clientY - session.startClientY,
      );
      const direction = resolveDirection(dx, dy);
      if (!direction) {
        return;
      }

      // Only suppress the click that belongs to this gesture — not later taps.
      // A sticky boolean stays true when the browser never fires a post-swipe
      // click (common with pointer drag), and then eats the next CTA press.
      suppressClickUntilRef.current = performance.now() + SUPPRESS_CLICK_MS;
      onSwipeRef.current(direction);
    },
    [enabled, resolveDirection],
  );

  const onPointerCancel = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      const session = sessionRef.current;
      if (session && session.pointerId === event.pointerId) {
        sessionRef.current = null;
      }
    },
    [],
  );

  const onClickCapture = useCallback((event: ReactMouseEvent<HTMLElement>) => {
    if (performance.now() >= suppressClickUntilRef.current) {
      return;
    }
    suppressClickUntilRef.current = 0;
    event.preventDefault();
    event.stopPropagation();
  }, []);

  return {
    onPointerDown,
    onPointerUp,
    onPointerCancel,
    onClickCapture,
    style: { touchAction: 'none' as const },
  };
}
